import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { ArrowLeft, RefreshCw, Clock, CreditCard, AlertTriangle, CheckCircle, XCircle, HelpCircle, Mail, Calendar } from 'lucide-react';

const RefundPolicy = () => {
  const sections = [
    {
      icon: CheckCircle,
      title: "1. Annulations avec remboursement intégral",
      content: `Vous bénéficiez d'un remboursement intégral dans les cas suivants :

Annulation par le patient :
• Annulation effectuée plus de 24 heures avant le rendez-vous
• Annulation via l'application ou en contactant le support

Annulation par le professionnel :
• Le praticien annule le rendez-vous (quelle que soit la raison)
• Le praticien ne se présente pas à la consultation

Problèmes techniques :
• Impossibilité de se connecter à la téléconsultation (problème côté DOCKHTA)
• Panne de la plateforme empêchant la consultation

Dans tous ces cas, le remboursement est automatique et effectué sous 3 à 5 jours ouvrés.`
    },
    {
      icon: Clock,
      title: "2. Annulations tardives (moins de 24h)",
      content: `Pour les annulations effectuées moins de 24 heures avant le rendez-vous :

Politique standard :
• 50% du montant de la consultation peut être retenu
• Cette retenue compense le temps réservé par le praticien

Exceptions (remboursement intégral accordé) :
• Urgence médicale justifiée
• Décès d'un proche
• Accident ou hospitalisation
• Cas de force majeure

Pour bénéficier d'une exception, contactez notre support avec un justificatif dans les 48 heures suivant l'annulation.`
    },
    {
      icon: XCircle,
      title: "3. Cas non remboursables",
      content: `Le remboursement n'est pas possible dans les situations suivantes :

• Non-présentation du patient sans annulation préalable
• Retard de plus de 15 minutes du patient (considéré comme absence)
• Consultation effectuée (même partiellement)
• Insatisfaction concernant l'avis médical reçu
• Médicaments ou traitements prescrits par le praticien

Note importante :
DOCKHTA est une plateforme de mise en relation. Les actes médicaux réalisés par les professionnels de santé relèvent de leur responsabilité propre.`
    },
    {
      icon: CreditCard,
      title: "4. Modes de remboursement",
      content: `Les remboursements sont effectués selon le mode de paiement original :

Mobile Money (Orange Money, MTN MoMo) :
• Remboursement sur le même numéro de téléphone
• Délai : 24 à 72 heures

Carte bancaire :
• Remboursement sur la carte utilisée
• Délai : 5 à 10 jours ouvrés (selon votre banque)

Portefeuille DOCKHTA (si disponible) :
• Crédit instantané sur votre solde
• Utilisable pour vos prochaines consultations`
    },
    {
      icon: Calendar,
      title: "5. Soins à domicile",
      content: `Pour les demandes de soins à domicile :

Annulation avant attribution d'un professionnel :
• Remboursement intégral automatique

Annulation après attribution :
• Plus de 4 heures avant : remboursement intégral
• Moins de 4 heures avant : 70% remboursé
• Non-présentation : aucun remboursement

Si le professionnel ne se présente pas :
• Remboursement intégral automatique
• Possibilité de reprogrammer gratuitement`
    },
    {
      icon: RefreshCw,
      title: "6. Procédure de remboursement",
      content: `Pour demander un remboursement :

1. Connectez-vous à votre compte DOCKHTA
2. Allez dans "Mes rendez-vous"
3. Sélectionnez le rendez-vous concerné
4. Cliquez sur "Demander un remboursement"
5. Indiquez le motif de votre demande

Vous pouvez également contacter notre support :
• Email : remboursements@dockhta.com
• Téléphone : +237 621 05 53 65

Délai de traitement :
• Demandes standards : 24 à 48 heures
• Cas nécessitant vérification : jusqu'à 5 jours ouvrés`
    },
    {
      icon: AlertTriangle,
      title: "7. Litiges et réclamations",
      content: `En cas de désaccord sur un remboursement :

Étape 1 - Support client
• Contactez notre équipe support
• Fournissez tous les détails et justificatifs
• Délai de réponse : 48 heures

Étape 2 - Service réclamations
• Si le problème persiste, envoyez un email à : reclamations@dockhta.com
• Incluez votre numéro de dossier
• Délai de réponse : 5 jours ouvrés

Étape 3 - Médiation
• En dernier recours, vous pouvez faire appel à un médiateur
• Les coordonnées sont disponibles sur demande

Nous nous engageons à traiter chaque réclamation avec équité et transparence.`
    },
    {
      icon: HelpCircle,
      title: "8. Questions fréquentes",
      content: `Q : Combien de temps ai-je pour demander un remboursement ?
R : Vous avez 30 jours après la date du rendez-vous pour effectuer une demande.

Q : Puis-je obtenir un avoir au lieu d'un remboursement ?
R : Oui, vous pouvez choisir un avoir valable 6 mois sur votre compte DOCKHTA.

Q : Le remboursement inclut-il les frais de service ?
R : Oui, le remboursement intégral inclut tous les frais payés.

Q : Comment savoir si mon remboursement a été traité ?
R : Vous recevrez un email de confirmation avec les détails du remboursement.`
    },
    {
      icon: Mail,
      title: "9. Contact",
      content: `Pour toute question concernant les remboursements :

Service Remboursements DOCKHTA
Email : remboursements@dockhta.com
Téléphone : +237 621 05 53 65
Horaires : Lundi - Vendredi, 8h - 18h

Adresse postale :
DOCKHTA
Yaoundé, Cameroun

Nous nous engageons à vous répondre dans les meilleurs délais.`
    }
  ];

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="bg-white border-b sticky top-0 z-50">
        <div className="max-w-4xl mx-auto px-4 py-4 flex items-center justify-between">
          <Link to="/">
            <Button variant="ghost" className="gap-2">
              <ArrowLeft className="w-4 h-4" />
              Retour à l&apos;accueil
            </Button>
          </Link>
          <Link to="/" className="text-2xl font-bold text-[#E53935]" style={{ fontFamily: 'Outfit, sans-serif' }}>
            DOCKHTA
          </Link>
        </div>
      </header>

      {/* Content */}
      <main className="max-w-4xl mx-auto px-4 py-12">
        <div className="text-center mb-12">
          <div className="w-16 h-16 bg-[#4CAF50]/10 rounded-2xl flex items-center justify-center mx-auto mb-6">
            <RefreshCw className="w-8 h-8 text-[#4CAF50]" />
          </div>
          <h1 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>
            Politique de Remboursement
          </h1>
          <p className="text-slate-600">
            Dernière mise à jour : Juin 2026
          </p>
        </div>

        {/* Summary Card */}
        <Card className="border-0 shadow-sm mb-8 bg-gradient-to-r from-[#4CAF50]/5 to-[#4CAF50]/10">
          <CardContent className="p-6 md:p-8">
            <h2 className="text-lg font-semibold text-slate-900 mb-4 flex items-center gap-2">
              <CheckCircle className="w-5 h-5 text-[#4CAF50]" />
              En résumé
            </h2>
            <div className="grid md:grid-cols-3 gap-4">
              <div className="bg-white rounded-lg p-4 text-center">
                <p className="text-2xl font-bold text-[#4CAF50]">100%</p>
                <p className="text-sm text-slate-600">Remboursé si annulation &gt; 24h</p>
              </div>
              <div className="bg-white rounded-lg p-4 text-center">
                <p className="text-2xl font-bold text-[#FF9800]">50%</p>
                <p className="text-sm text-slate-600">Retenu si annulation &lt; 24h</p>
              </div>
              <div className="bg-white rounded-lg p-4 text-center">
                <p className="text-2xl font-bold text-[#1976D2]">3-5 jours</p>
                <p className="text-sm text-slate-600">Délai de remboursement</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <div className="space-y-6">
          {sections.map((section, index) => {
            const IconComponent = section.icon;
            return (
              <Card key={index} className="border-0 shadow-sm">
                <CardContent className="p-6 md:p-8">
                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 bg-[#4CAF50]/10 rounded-lg flex items-center justify-center flex-shrink-0">
                      <IconComponent className="w-5 h-5 text-[#4CAF50]" />
                    </div>
                    <div>
                      <h2 className="text-lg font-semibold text-slate-900 mb-3">
                        {section.title}
                      </h2>
                      <div className="text-slate-600 whitespace-pre-line leading-relaxed">
                        {section.content}
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>

        {/* CTA */}
        <Card className="mt-12 border-0 shadow-sm bg-gradient-to-r from-[#4CAF50] to-[#388E3C] text-white">
          <CardContent className="p-8 text-center">
            <h3 className="text-xl font-semibold mb-2">
              Besoin d&apos;aide pour un remboursement ?
            </h3>
            <p className="text-white/80 mb-6">
              Notre équipe support est là pour vous accompagner
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/contact">
                <Button className="bg-white text-[#4CAF50] hover:bg-white/90">
                  <Mail className="w-4 h-4 mr-2" />
                  Nous contacter
                </Button>
              </Link>
              <Link to="/faq">
                <Button variant="outline" className="border-white text-white hover:bg-white/20">
                  <HelpCircle className="w-4 h-4 mr-2" />
                  Consulter la FAQ
                </Button>
              </Link>
            </div>
          </CardContent>
        </Card>

        {/* Footer links */}
        <div className="mt-12 text-center text-slate-500 text-sm">
          <p>
            Cette politique fait partie intégrante de nos Conditions Générales d&apos;Utilisation.
          </p>
          <div className="flex justify-center gap-4 mt-4">
            <Link to="/terms" className="text-[#4CAF50] hover:underline">
              Conditions d&apos;Utilisation
            </Link>
            <span>•</span>
            <Link to="/privacy" className="text-[#4CAF50] hover:underline">
              Politique de Confidentialité
            </Link>
          </div>
        </div>
      </main>
    </div>
  );
};

export default RefundPolicy;
